import { c as _c } from "react/compiler-runtime";
import React from "react";
import { formatDate } from "@/lib/format-data";
interface WeightInsightsProps {
  changeSinceLastVisit: number | null;
  totalWeightChange: number | null;
  previousDate: string | null;
  firstDate: string | null;
  isLoading?: boolean;
}
function formatChange(value: number): string {
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(1)} lbs`;
}

/**
 * Returns the color class based on weight change direction.
 * Loss (negative) = green (success), Gain (positive) = amber (warning).
 */
function getChangeColor(value: number): string {
  if (value < 0) return "text-status-success-text";
  if (value > 0) return "text-status-warning-text";
  return "text-foreground-secondary";
}

/**
 * Displays weight change insights with color-coded formatting.
 * Weight loss is shown in green, weight gain in amber/yellow.
 */
export const WeightInsights: React.FC<WeightInsightsProps> = t0 => {
  const $ = _c(18);
  const {
    changeSinceLastVisit,
    totalWeightChange,
    previousDate,
    firstDate,
    isLoading: t1
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  if (isLoading) {
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="flex items-center gap-3" data-testid="weight-insights-skeleton"><div className="h-3.5 w-40 animate-pulse rounded bg-muted" /><div className="h-3.5 w-36 animate-pulse rounded bg-muted" /></div>;
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    return t2;
  }
  if (changeSinceLastVisit === null) {
    return null;
  }
  const showTotalChange = totalWeightChange !== null && firstDate !== null && previousDate !== null && firstDate !== previousDate;
  let t2;
  if ($[1] !== changeSinceLastVisit) {
    t2 = getChangeColor(changeSinceLastVisit);
    $[1] = changeSinceLastVisit;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== changeSinceLastVisit) {
    t3 = formatChange(changeSinceLastVisit);
    $[3] = changeSinceLastVisit;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== previousDate) {
    t4 = previousDate && <span className="text-foreground-tertiary">{" "}({formatDate(previousDate, true, false)})</span>;
    $[5] = previousDate;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2 || $[8] !== t3 || $[9] !== t4) {
    t5 = <span className={t2}>{t3} since last visit{t4}</span>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== firstDate || $[12] !== showTotalChange || $[13] !== totalWeightChange) {
    t6 = showTotalChange && <><span className="text-foreground-tertiary">·</span><span className={getChangeColor(totalWeightChange)}>{formatChange(totalWeightChange)} total{firstDate && <span className="text-foreground-tertiary">{" "}(since {formatDate(firstDate, true, true)})</span>}</span></>;
    $[11] = firstDate;
    $[12] = showTotalChange;
    $[13] = totalWeightChange;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t5 || $[16] !== t6) {
    t7 = <div className="flex items-center gap-3 text-caption">{t5}{t6}</div>;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
};